"use client";

import { Alert } from "@/components/ui/alert";
import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import { createPortal } from "react-dom";
import { SABINE_NAME } from "@/lib/sabine";
import AIChatComposer from "@/components/AIChatComposer";
import AIChatHeader from "@/components/AIChatHeader";
import AIChatSuggestedPrompts from "@/components/AIChatSuggestedPrompts";
import { useCoachPane } from "@/components/nav/NavCoachPane";
import {
  NAV_ARRIVE,
  SABINE_SOLID,
  arriveStep,
} from "@/components/nav/nav-styles";
import { Badge } from "@/components/ui/badge";
import { TooltipProvider } from "@/components/ui/tooltip";
import { renderAIChatMarkdown } from "@/lib/ai-chat-markdown";
import { useToast } from "@/components/ui/use-toast";
import SmallLoadingSpinner from "@/components/SmallLoadingSpinner";
import AIChatFeedbackButtons from "@/components/AIChatFeedbackButtons";
import AIChatCopyButton from "@/components/AIChatCopyButton";
import AgentActionLinks from "@/components/agent-actions/AgentActionLinks";
import { cn } from "@/lib/utils";
import { useSubmit } from "@/hooks/useSubmit";
import { createAIChatFeedback } from "@/server-actions/ai-chat-feedback/actions";
import {
  AI_CHAT_FEEDBACK_CAPTURE_STATUS,
  AI_CHAT_FEEDBACK_PENDING_ANSWER,
  buildAIChatFeedbackMessageId,
} from "@/lib/ai-chat-feedback-utils";

const getMessageId = (message, index) =>
  message?.id || `${message?.role || "message"}-${index}`;
const MAX_FALLBACK_RESPONSE_TIME_MS = 10 * 60 * 1000;
// The thinking dots pulse in turn; full class names so Tailwind sees them.
const THINKING_DOT_DELAYS = [
  "[animation-delay:0s]",
  "[animation-delay:0.15s]",
  "[animation-delay:0.3s]",
];

function getMessageTimestampMs(message) {
  const rawValue = message?.createdAt ?? message?.timestamp;
  if (!rawValue) return null;

  const timestamp =
    typeof rawValue === "number" ? rawValue : new Date(rawValue).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function formatRelativeTime(timestampMs, nowMs) {
  if (timestampMs == null || nowMs == null) return null;
  const diffSeconds = Math.max(0, Math.round((nowMs - timestampMs) / 1000));
  if (diffSeconds < 45) return "now";
  const diffMinutes = Math.round(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.round(diffHours / 24);
  return `${diffDays}d ago`;
}

// Re-exported so existing `import { AIChatCopyButton } from "@/components/AIChat"`
// callers keep working; the component itself lives in its own file.
export { AIChatCopyButton };

/**
 * Sabine's chat: the thread, the suggested prompts and the composer, filling
 * whatever holds it. Pages register it with the side nav (`SideNavSlot
 * coach`), which shows it in the coach pane beside the icon rail; the pane
 * tells it, through `useCoachPane`, whether it is showing and which header
 * controls (close, fullscreen) to offer. Rendered anywhere else it fills its
 * parent and shows neither.
 *
 * @param {Object} props - Component props
 * @param {Array} props.messages - Array of message objects with id, role, content
 * @param {boolean} props.isProcessing - Whether AI is currently processing
 * @param {string} props.inputValue - Current input field value
 * @param {Function} props.onInputChange - Callback for input field changes
 * @param {Function} props.setInputValue - Optional state setter for input value (used by voice input)
 * @param {Function} props.onSubmit - Callback for form submission (receives enableWebSearch flag)
 * @param {Array} props.prompts - Suggested prompt options (string or { label, prompt })
 * @param {Function} props.onPromptSelect - Callback when a suggested prompt is selected
 * @param {Array} props.summaryChips - Array of summary chip objects with label and value
 * @param {Array} props.lessons - Array of lesson objects with id, title, url
 * @param {boolean} props.contextLoading - Whether context is being loaded
 * @param {string} props.error - Error message if any
 * @param {Function} props.onReset - Callback for reset button
 * @param {boolean} props.showWebSearchToggle - Whether to show web search toggle (optional)
 * @param {string} props.headerTitle - Optional title for the panel
 * @param {string} props.headerDescription - Optional helper copy below the title
 * @param {Function} props.renderMessageContent - Optional renderer for message body content
 * @param {Function} props.renderProcessingContent - Optional renderer for processing state content
 * @param {string} props.feedbackSource - Optional label for feedback source (maps to enum)
 * @param {Object} props.feedbackContext - Optional context metadata stored with feedback
 * @param {boolean} props.captureFeedback - Whether this component logs each Q&A itself (default: showFeedback). False when the chat's route already logs them, so the thumbs still show but nothing is written twice
 * @param {string} props.contextBadge - Optional short label shown as a pill in the header (e.g. "Analyzing Jane Doe")
 */
export default function AIChat({
  messages,
  isProcessing,
  inputValue,
  onInputChange,
  setInputValue,
  onSubmit,
  prompts,
  onPromptSelect,
  summaryChips,
  lessons,
  contextLoading,
  error,
  onReset,
  showWebSearchToggle = false,
  headerTitle = "Ask Studio",
  headerDescription = "Ask about this simulation to get targeted coaching.",
  renderMessageContent,
  renderProcessingContent,
  feedbackSource = "GENERIC",
  feedbackContext = {},
  // When false, AIChat renders no feedback buttons and runs no feedback
  // capture — the caller owns feedback (e.g. the playbook renders its own
  // inside renderMessageContent), so we avoid double-logging.
  showFeedback = true,
  captureFeedback = showFeedback,
  showCopyAction = true,
  shareConfig = null,
  contextBadge = null,
  inputPlaceholder = "Ask for coaching…",
  belowMessagesContent = null,
}) {
  const { toast } = useToast();
  const { open, onClose, isFullscreen, onFullscreenChange, headerSlot } =
    useCoachPane();
  const scrollRef = useRef(null);
  // Enter sends, Shift+Enter starts a new line.
  const { formRef, onKeyDown: submitOnEnter } = useSubmit();
  const [enableWebSearch, setEnableWebSearch] = useState(true);
  const [isSharing, setIsSharing] = useState(false);
  const [loggedMessageIds, setLoggedMessageIds] = useState(new Set());
  const pendingLoggedMessageIdsRef = useRef(new Set());
  const completedLoggedMessageIdsRef = useRef(new Set());
  const errorLoggedMessageIdsRef = useRef(new Set());
  const feedbackResetVersionRef = useRef(0);
  const userMessageTimesRef = useRef(new Map());
  const assistantResponseTimesRef = useRef(new Map());
  const messageSeenAtRef = useRef(new Map());
  // `now` stays null until after mount so the server and first client render
  // omit relative timestamps identically (no hydration mismatch); the interval
  // then keeps "1m ago / 2m ago" fresh while the panel is open.
  const [now, setNow] = useState(null);
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    setNow(Date.now());
    const intervalId = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(intervalId);
  }, []);

  const getDisplayTimestampMs = useCallback((message, index) => {
    const realTimestamp = getMessageTimestampMs(message);
    if (realTimestamp != null) return realTimestamp;
    const key = getMessageId(message, index);
    const seen = messageSeenAtRef.current;
    if (!seen.has(key)) seen.set(key, Date.now());
    return seen.get(key);
  }, []);

  // Clicking a suggested prompt fills the input (via the parent's
  // onPromptSelect) and then auto-submits once the value lands — so a tap goes
  // straight in without the user pressing send. Works across every parent
  // because we wait for the controlled inputValue to update.
  const pendingPromptSubmitRef = useRef(false);

  useEffect(() => {
    if (!pendingPromptSubmitRef.current) return;
    if (isProcessing || !inputValue?.trim()) return;
    pendingPromptSubmitRef.current = false;
    // Trigger the real form submit so the synthetic event carries a proper
    // `event.target` (the form). Some consumers read `new FormData(event.target)`
    // in their submit handler, which a hand-rolled `{ preventDefault }` breaks.
    formRef.current?.requestSubmit();
  }, [inputValue, isProcessing]);

  const handlePromptClick = (value) => {
    if (isProcessing) return;
    onPromptSelect?.(value);
    pendingPromptSubmitRef.current = true;
  };

  const resetFeedbackCaptureState = useCallback(() => {
    feedbackResetVersionRef.current += 1;
    setLoggedMessageIds(new Set());
    pendingLoggedMessageIdsRef.current.clear();
    completedLoggedMessageIdsRef.current.clear();
    errorLoggedMessageIdsRef.current.clear();
    userMessageTimesRef.current.clear();
    assistantResponseTimesRef.current.clear();
  }, []);

  useEffect(() => {
    if ((messages?.length || 0) > 1) return;
    resetFeedbackCaptureState();
  }, [messages?.length, resetFeedbackCaptureState]);

  useEffect(() => {
    if (!open) return;
    const container = scrollRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, isProcessing, open]);

  useEffect(() => {
    if (!captureFeedback) return;
    if (!messages?.length) return;

    messages.forEach((message, index) => {
      if (message?.role === "user") {
        const messageId = getMessageId(message, index);
        const questionContent =
          typeof message.content === "string"
            ? message.content.trim()
            : typeof message.text === "string"
              ? message.text.trim()
              : "";
        if (!userMessageTimesRef.current.has(messageId)) {
          const timestamp = getMessageTimestampMs(message) ?? Date.now();
          userMessageTimesRef.current.set(messageId, timestamp);
        }
        const feedbackMessageId = buildAIChatFeedbackMessageId(
          feedbackSource,
          messageId,
        );
        if (
          questionContent &&
          !pendingLoggedMessageIdsRef.current.has(feedbackMessageId)
        ) {
          const captureVersion = feedbackResetVersionRef.current;
          pendingLoggedMessageIdsRef.current.add(feedbackMessageId);
          createAIChatFeedback({
            question: questionContent,
            answer: AI_CHAT_FEEDBACK_PENDING_ANSWER,
            source: feedbackSource,
            contextId: feedbackContext?.contextId,
            contextType: feedbackContext?.contextType,
            resultId: feedbackContext?.resultId,
            resultType: feedbackContext?.resultType,
            workspaceId: feedbackContext?.workspaceId,
            messageId: feedbackMessageId,
            metadata: {
              ...feedbackContext,
              captureStatus: AI_CHAT_FEEDBACK_CAPTURE_STATUS.PENDING,
              userMessageId: messageId,
            },
          }).catch((error) => {
            if (captureVersion === feedbackResetVersionRef.current) {
              pendingLoggedMessageIdsRef.current.delete(feedbackMessageId);
            }
            console.error("[AIChat] Failed to log pending feedback", error);
          });
        }
        return;
      }

      if (message?.role !== "assistant") return;
      const messageId = getMessageId(message, index);
      const isLatestMessage = index === messages.length - 1;
      if (isProcessing && isLatestMessage) return;

      const previousUserMessageData = (() => {
        for (let i = index - 1; i >= 0; i--) {
          if (messages[i]?.role === "user") {
            return { message: messages[i], index: i };
          }
        }
        return null;
      })();
      const previousUserMessage = previousUserMessageData?.message || null;
      const previousUserMessageId = previousUserMessage
        ? getMessageId(previousUserMessage, previousUserMessageData.index)
        : null;
      const feedbackMessageId = previousUserMessageId
        ? buildAIChatFeedbackMessageId(feedbackSource, previousUserMessageId)
        : messageId;
      if (
        loggedMessageIds.has(feedbackMessageId) ||
        completedLoggedMessageIdsRef.current.has(feedbackMessageId)
      ) {
        return;
      }

      const feedbackQuestion =
        message.question ||
        previousUserMessage?.content ||
        previousUserMessage?.text ||
        "";
      const answerContent =
        typeof message.content === "string" ? message.content : "";
      let responseTimeMs =
        typeof message.responseTimeMs === "number"
          ? message.responseTimeMs
          : undefined;

      if (!responseTimeMs && previousUserMessage) {
        const startedAt = userMessageTimesRef.current.get(
          previousUserMessageId,
        );
        if (typeof startedAt === "number") {
          const assistantTimestamp = getMessageTimestampMs(message);
          const computed =
            typeof assistantTimestamp === "number"
              ? assistantTimestamp - startedAt
              : isLatestMessage
                ? Date.now() - startedAt
                : null;
          if (Number.isFinite(computed) && computed > 0) {
            if (
              typeof assistantTimestamp === "number" ||
              computed <= MAX_FALLBACK_RESPONSE_TIME_MS
            ) {
              responseTimeMs = computed;
              assistantResponseTimesRef.current.set(messageId, responseTimeMs);
            }
          }
        }
      } else if (responseTimeMs) {
        assistantResponseTimesRef.current.set(messageId, responseTimeMs);
      }

      if (!feedbackQuestion || !answerContent) return;

      const captureVersion = feedbackResetVersionRef.current;
      completedLoggedMessageIdsRef.current.add(feedbackMessageId);
      startTransition(() => {
        createAIChatFeedback({
          question: feedbackQuestion,
          answer: answerContent,
          isPositive: message.isPositive ?? null,
          source: feedbackSource,
          contextId: feedbackContext?.contextId,
          contextType: feedbackContext?.contextType,
          resultId: feedbackContext?.resultId,
          resultType: feedbackContext?.resultType,
          workspaceId: feedbackContext?.workspaceId,
          messageId: feedbackMessageId,
          responseTimeMs,
          metadata: {
            ...feedbackContext,
            captureStatus: AI_CHAT_FEEDBACK_CAPTURE_STATUS.COMPLETED,
            assistantMessageId: messageId,
            userMessageId: previousUserMessageId,
          },
        })
          .then(() => {
            if (captureVersion !== feedbackResetVersionRef.current) return;
            setLoggedMessageIds((current) => {
              const next = new Set(current);
              next.add(feedbackMessageId);
              return next;
            });
          })
          .catch((error) => {
            if (captureVersion === feedbackResetVersionRef.current) {
              completedLoggedMessageIdsRef.current.delete(feedbackMessageId);
            }
            console.error("[AIChat] Failed to log feedback", error);
          });
      });
    });
  }, [
    captureFeedback,
    messages,
    feedbackContext,
    feedbackSource,
    loggedMessageIds,
    isProcessing,
  ]);

  useEffect(() => {
    if (!captureFeedback) return;
    const errorMessage = typeof error === "string" ? error.trim() : "";
    if (!errorMessage || !messages?.length) return;

    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (message?.role !== "user") continue;

      const questionContent =
        typeof message.content === "string"
          ? message.content.trim()
          : typeof message.text === "string"
            ? message.text.trim()
            : "";
      if (!questionContent) return;

      const userMessageId = getMessageId(message, i);
      const feedbackMessageId = buildAIChatFeedbackMessageId(
        feedbackSource,
        userMessageId,
      );
      if (errorLoggedMessageIdsRef.current.has(feedbackMessageId)) return;
      const captureVersion = feedbackResetVersionRef.current;
      errorLoggedMessageIdsRef.current.add(feedbackMessageId);

      const startedAt = userMessageTimesRef.current.get(userMessageId);
      const responseTimeMs =
        typeof startedAt === "number"
          ? Math.max(Date.now() - startedAt, 0)
          : undefined;

      createAIChatFeedback({
        question: questionContent,
        answer: errorMessage,
        source: feedbackSource,
        contextId: feedbackContext?.contextId,
        contextType: feedbackContext?.contextType,
        resultId: feedbackContext?.resultId,
        resultType: feedbackContext?.resultType,
        workspaceId: feedbackContext?.workspaceId,
        messageId: feedbackMessageId,
        responseTimeMs,
        metadata: {
          ...feedbackContext,
          captureStatus: AI_CHAT_FEEDBACK_CAPTURE_STATUS.ERROR,
          userMessageId,
          errorMessage,
        },
      }).catch((loggingError) => {
        if (captureVersion === feedbackResetVersionRef.current) {
          errorLoggedMessageIdsRef.current.delete(feedbackMessageId);
        }
        console.error("[AIChat] Failed to log error feedback", loggingError);
      });
      return;
    }
  }, [captureFeedback, error, messages, feedbackContext, feedbackSource]);

  const handleShare = async () => {
    if (!shareConfig || isSharing) return;
    try {
      setIsSharing(true);
      const response = await fetch("/api/chats/share", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contextType: shareConfig.contextType,
          contextId: shareConfig.contextId,
          contextOwnerUserId: shareConfig.contextOwnerUserId,
          workspaceId: shareConfig.workspaceId,
          contextTitle: shareConfig.contextTitle,
          contextLabel: shareConfig.contextLabel,
          sessionSource: shareConfig.sessionSource,
          sessionContextId: shareConfig.sessionContextId,
          headerTitle,
          headerDescription,
          contextSnapshot: shareConfig.contextSnapshot,
          summaryChips,
          messages,
          contextPath:
            typeof window !== "undefined"
              ? `${window.location.pathname}${window.location.search || ""}`
              : "",
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.chatId) {
        throw new Error(payload?.error || "Unable to create a shared chat.");
      }
      const shareUrl =
        typeof window !== "undefined"
          ? `${window.location.origin}/chats/${payload.chatId}`
          : `/chats/${payload.chatId}`;
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: "Chat link copied",
        description: "Share the link to continue this conversation together.",
      });
    } catch (error) {
      toast({
        title: "Unable to share chat",
        description:
          error?.message || "We couldn't create a shared chat right now.",
        variant: "destructive",
      });
    } finally {
      setIsSharing(false);
    }
  };

  const lastAssistantIndex =
    messages
      .map((m, i) => ({ m, i }))
      .filter(({ m }) => m.role === "assistant")
      .at(-1)?.i ?? -1;
  const hasUserMessages = messages.some((m) => m.role === "user");
  // The chips sit under Sabine's opening message, or stand alone when the
  // thread is empty (the home chat opens with nothing said yet).
  const suggestions = !isProcessing && !hasUserMessages && (
    <AIChatSuggestedPrompts
      prompts={prompts}
      disabled={isProcessing}
      onPick={handlePromptClick}
    />
  );

  const header = (
    <AIChatHeader
      ink={Boolean(headerSlot)}
      contextBadge={contextBadge}
      onReset={() => {
        resetFeedbackCaptureState();
        onReset?.();
      }}
      resetDisabled={isProcessing}
      onShare={shareConfig && hasUserMessages ? handleShare : null}
      isSharing={isSharing}
      shareDisabled={isSharing || isProcessing}
      isFullscreen={isFullscreen}
      onFullscreenChange={onFullscreenChange}
      onClose={onClose}
    />
  );

  return (
    <TooltipProvider delayDuration={300}>
      <div
        data-testid="ai-chat-panel"
        className="flex h-full w-full flex-col overflow-hidden"
      >
        {/* Beside the rail the header sits in the navy top bar, right above
            the column (useCoachPane's headerSlot); elsewhere, at its top. */}
        {headerSlot ? createPortal(header, headerSlot) : header}

        {/* Messages Area */}
        {/* Beside the rail the thread and the composer come in after the
            header as the column opens (NAV_ARRIVE). */}
        <div
          ref={scrollRef}
          className={cn(
            NAV_ARRIVE,
            "flex-1 overflow-y-auto px-5 pt-4 space-y-4 w-full flex justify-center overscroll-y-contain",
          )}
          style={arriveStep(1)}
        >
          <div className="max-w-4xl w-full space-y-5">
            {/* Summary Chips */}
            {summaryChips?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {summaryChips.map((chip) => (
                  <Badge
                    key={`${chip.label}-${chip.value}`}
                    tone="neutral"
                    size="sm"
                  >
                    <span className="font-normal text-gray-500">
                      {chip.label}:
                    </span>
                    &nbsp;{chip.value}
                  </Badge>
                ))}
                {contextLoading && (
                  <Badge tone="info" variant="soft" size="sm" className="gap-1">
                    <SmallLoadingSpinner size={12} />
                    Syncing context…
                  </Badge>
                )}
              </div>
            )}

            {/* Workspace Lessons */}
            {lessons?.length > 0 && (
              <div className="space-y-3">
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 p-3">
                  <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-indigo-700">
                    <span>Workspace Lessons</span>
                    <span className="text-[10px] text-indigo-500">Context</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {lessons.slice(0, 4).map((lesson) => (
                      <a
                        key={lesson.id}
                        href={lesson.url || "#"}
                        target={lesson.url ? "_blank" : undefined}
                        rel={lesson.url ? "noreferrer" : undefined}
                        className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white/80 px-3 py-1 text-xs font-medium text-indigo-700 transition hover:border-indigo-400"
                      >
                        {lesson.title}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {messages.length === 0 && suggestions}

            {messages.map((message, index) => {
              const isAssistant = message.role === "assistant";
              const messageKey = getMessageId(message, index);
              const isLatestMessage = index === messages.length - 1;
              const deferFeedback = isProcessing && isLatestMessage;
              const previousUserMessage = (() => {
                for (let i = index - 1; i >= 0; i--) {
                  if (messages[i]?.role === "user") {
                    return { message: messages[i], index: i };
                  }
                }
                return null;
              })();
              const previousUserMessageRecord =
                previousUserMessage?.message || null;
              const previousUserMessageId = previousUserMessageRecord
                ? getMessageId(
                    previousUserMessageRecord,
                    previousUserMessage.index,
                  )
                : null;
              const feedbackRecordMessageId = previousUserMessageId
                ? buildAIChatFeedbackMessageId(
                    feedbackSource,
                    previousUserMessageId,
                  )
                : messageKey;
              const feedbackQuestion =
                message.question ||
                previousUserMessageRecord?.content ||
                previousUserMessageRecord?.text ||
                "";
              const answerContent =
                typeof message.content === "string" ? message.content : "";
              const cachedResponseTimeMs =
                typeof message.responseTimeMs === "number"
                  ? message.responseTimeMs
                  : assistantResponseTimesRef.current.get(messageKey);
              const content = renderMessageContent ? (
                renderMessageContent(message, { isAssistant, index })
              ) : (
                <div className="flex-1 min-w-0 text-sm text-slate-800">
                  <div
                    className="prose prose-sm max-w-none text-slate-800 overflow-x-auto"
                    dangerouslySetInnerHTML={{
                      __html: renderAIChatMarkdown(message.content || ""),
                    }}
                  />
                  {/* Where the answer sends you, as a button rather than a link
                    to find mid-paragraph — the affordance the home hero has
                    always had. A caller that renders its own message body owns
                    its own action row (the reports chat shows model-verified
                    destinations instead), so this is the default path only. */}
                  {isAssistant ? (
                    <AgentActionLinks
                      content={message.content || ""}
                      className="mt-3"
                    />
                  ) : null}
                </div>
              );
              const relativeTime = formatRelativeTime(
                getDisplayTimestampMs(message, index),
                now,
              );
              if (!isAssistant) {
                return (
                  <div
                    key={messageKey}
                    className="flex flex-col items-end gap-1 animate-message-in"
                  >
                    <div
                      data-testid="ai-chat-message-user"
                      className={cn(
                        "max-w-[80%] rounded-2xl rounded-tr-sm px-4 py-2.5",
                        SABINE_SOLID,
                      )}
                    >
                      <div className="whitespace-pre-wrap text-sm leading-relaxed">
                        {message.content}
                      </div>
                    </div>
                    {relativeTime ? (
                      <span className="px-1 text-[11px] text-slate-400">
                        {relativeTime}
                      </span>
                    ) : null}
                  </div>
                );
              }

              return (
                <div
                  key={messageKey}
                  data-testid="ai-chat-message-assistant"
                  className="space-y-1.5 animate-message-in"
                >
                  <p className="text-xs font-semibold text-slate-400">
                    {SABINE_NAME}
                  </p>
                  <div className="min-w-0 space-y-3">
                    {content}
                    <div className="flex items-center">
                      {showFeedback &&
                      !deferFeedback &&
                      feedbackQuestion &&
                      answerContent ? (
                        <AIChatFeedbackButtons
                          question={feedbackQuestion}
                          answer={answerContent}
                          source={feedbackSource}
                          contextId={feedbackContext?.contextId}
                          contextType={feedbackContext?.contextType}
                          workspaceId={feedbackContext?.workspaceId}
                          messageId={feedbackRecordMessageId}
                          responseTimeMs={cachedResponseTimeMs}
                          trailingAction={
                            showCopyAction ? (
                              <AIChatCopyButton text={answerContent} />
                            ) : null
                          }
                          metadata={{
                            ...feedbackContext,
                            captureStatus:
                              AI_CHAT_FEEDBACK_CAPTURE_STATUS.COMPLETED,
                            assistantMessageId: messageKey,
                            userMessageId: previousUserMessageId,
                          }}
                        />
                      ) : showCopyAction && answerContent && !deferFeedback ? (
                        <AIChatCopyButton text={answerContent} />
                      ) : null}
                    </div>
                    {relativeTime ? (
                      <span className="block text-[11px] text-slate-400">
                        {relativeTime}
                      </span>
                    ) : null}
                    {index === lastAssistantIndex && suggestions}
                  </div>
                </div>
              );
            })}

            {/* Processing State */}
            {isProcessing && (
              <div
                data-testid="ai-chat-processing"
                className="space-y-1.5 animate-message-in"
              >
                <p className="text-xs font-semibold text-slate-400">
                  {SABINE_NAME}
                </p>
                <div className="min-w-0">
                  {renderProcessingContent ? (
                    renderProcessingContent()
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1" aria-hidden>
                        {THINKING_DOT_DELAYS.map((delay) => (
                          <span
                            key={delay}
                            className={cn(
                              "thinking-dot h-1.5 w-1.5 rounded-full",
                              SABINE_SOLID,
                              delay,
                            )}
                          />
                        ))}
                      </span>
                      {SABINE_NAME} is thinking…
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <Alert variant="destructive" data-testid="ai-chat-error">
                {error}
              </Alert>
            )}

            {/* Caller-supplied banners (e.g. playbook sync / empty states) */}
            {belowMessagesContent}
          </div>
        </div>

        <div className={cn(NAV_ARRIVE, "shrink-0")} style={arriveStep(2)}>
          <AIChatComposer
            formRef={formRef}
            onSubmit={onSubmit}
            inputValue={inputValue}
            onInputChange={onInputChange}
            setInputValue={setInputValue}
            onKeyDown={submitOnEnter}
            placeholder={inputPlaceholder}
            isProcessing={isProcessing}
            autoFocus={open}
            showWebSearchToggle={showWebSearchToggle}
            enableWebSearch={enableWebSearch}
            onToggleWebSearch={() => setEnableWebSearch((value) => !value)}
          />
        </div>
      </div>
    </TooltipProvider>
  );
}
